import {ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnInit, ViewChild} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {FormArray, FormBuilder} from '@angular/forms';
import {NcDocumentsService} from './nc-documents.service';
import {DocumentModel} from '../models/document-model';
import {LabelWithParameters, Option} from 'nc-shared';
import {ColumnBuilder, DatatableColumn, FilterConfig, SelectConfig, TableData} from 'nc-datatable';
import {StringsUtils} from 'nc-utils';

@Component({
  selector: 'nc-documents',
  templateUrl: './nc-documents.component.html',
  styleUrls: ['./nc-documents.component.scss'],
  providers: [NcDocumentsService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NcDocumentsComponent implements OnInit, OnChanges {
  @Input() formArray: FormArray;
  @Input() isReadonly = false;
  @Input() documentTypes$: Observable<Array<Option>>;
  @Input() onDownload: (rowData: DocumentModel) => void;
  @Input() onPreview: (rowData: DocumentModel) => void;
  @Input() onDownloadSelected: (selectedIds: number[]) => void;
  @Input() onDelete: (rowData: DocumentModel) => void;
  @Input() additionalColumns: DatatableColumn[];
  @Input() hidePreview = false;
  @Input() hideDownload = false;
  @Input() hideMultiDownload = false;
  @Input() id: string;
  @Input() buttonId: string;
  @Input() downloadButtonId: string;

  data$: BehaviorSubject<TableData> = new BehaviorSubject<TableData>({records: []});
  selectConfig: SelectConfig;
  filterConfig: FilterConfig;
  columns: Array<DatatableColumn>;

  @ViewChild('downloadLink') downloadLink: ElementRef;

  constructor(private fb: FormBuilder, private documentService: NcDocumentsService) {
    this.filterConfig = {
      showFilter: false,
      showGlobalFilter: true
    };
  }

  ngOnChanges(): void {
    this.ngOnInit();
  }

  ngOnInit(): void {
    this.documentService.initialize(this.formArray, this.data$);
    if (!this.isReadonly && !this.hideMultiDownload) {
      this.selectConfig = {
        identifierColumn: 'docmanId',
        formArray: this.fb.array([]),
        isFirstColumn: true
      };
    } else {
      this.selectConfig = null;
    }

    this.columns = this.getColumns();
  }

  download = (rowData) => {
    this.onDownload(rowData);
  }

  preview = (rowData: DocumentModel) => {
    this.onPreview(rowData);
  }

  downloadSelected = () => {
    const selectedIds = this.selectConfig.formArray.getRawValue().filter(x => x.value).map(ret => ret.value);
    this.onDownloadSelected(selectedIds);
  }

  getColumns = (): Array<DatatableColumn> => {
    let columns: Array<DatatableColumn> = [];
    columns = [
      ColumnBuilder.createHidden('id'),
      ColumnBuilder.create('documentTypeTranslation', 'documentType'),
      ColumnBuilder.create('nameWithExtension', 'name'),
    ];

    if (this.additionalColumns) {
      this.additionalColumns.forEach(column => columns.push(column));
    }

    if (!this.hideDownload) {
      columns.push(ColumnBuilder.createAction('download', 'download', this.download, {defaultIcon: 'download'}, this.isDownloadVisible));
    }

    if (!this.hidePreview) {
      columns.push(ColumnBuilder.createAction('preview', 'preview', this.preview, {defaultIcon: 'preview'}, this.isPreviewVisible));
    }

    if (!this.isReadonly) {
      columns.push(ColumnBuilder.createAction('delete', 'delete', this.delete, {defaultIcon: 'delete'}));
    }

    return columns;
  }

  isDownloadVisible = (rowData: DocumentModel): boolean => {
    return StringsUtils.isNotEmpty(rowData.nameWithExtension) && rowData.file === null;
  }

  isPreviewVisible = (rowData): boolean => {
    const name = rowData.nameWithExtension;
    return StringsUtils.isNotEmpty(name) && rowData.file === null
      && (name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.bmp'));
  }

  private delete = (rowData: DocumentModel): void => {
    if (rowData.id === 0) {
      this.documentService.delete(rowData, this.formArray);
    } else {
      this.onDelete(rowData);
    }
  }

  edit = (model: DocumentModel): void => {
    if (!this.isReadonly) {
      this.documentService.openDialog(model, this.formArray, this.documentTypes$);
    }
  }

  openDialog = (): void => {
    this.documentService.openDialog(null, this.formArray, this.documentTypes$);
  }

  isInvalid(): boolean {
    let isInvalid = false;
    const errors = this.formArray.errors;

    if (errors) {
      const errorKeys = Object.keys(errors);
      isInvalid = errorKeys.length > 0;
    }

    return isInvalid;
  }

  getErrorMessage(): LabelWithParameters {
    if (!this.isInvalid()) {
      return null;
    }

    let errorLabel = null;
    const errors = this.formArray.errors;

    if (errors) {
      const error = Object.keys(errors)[0];
      errorLabel = {label: error};
    }

    return errorLabel;
  }

  isAnySelected = (): boolean => {
    return this.selectConfig !== null ? this.selectConfig.formArray.getRawValue().filter(x => x.value).length > 0 : false;
  }
}
